
export interface CsvRecord {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  organization: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedRecord?: CsvRecord;
}

export const validateRecord = (row: Partial<CsvRecord>): ValidationResult => {
  const errors: string[] = [];
  
  // Required fields validation
  if (!row.email || typeof row.email !== 'string' || row.email.trim() === '') {
    errors.push('Email is required');
  } else {
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row.email.trim())) {
      errors.push('Invalid email format');
    }
  }
  
  if (!row.first_name || typeof row.first_name !== 'string' || row.first_name.trim() === '') {
    errors.push('First name is required');
  }
  
  if (!row.last_name || typeof row.last_name !== 'string' || row.last_name.trim() === '') {
    errors.push('Last name is required');
  }
  
  // Role validation
  if (row.role && !['coach', 'parent', 'player'].includes(row.role)) {
    errors.push('Invalid role. Must be one of: coach, parent, player');
  }
  
  if (!row.organization || typeof row.organization !== 'string' || row.organization.trim() === '') {
    errors.push('Organization is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// New function that matches the test expectations
export const validateCsvRecord = (record: CsvRecord): ValidationResult => {
  const errors: string[] = [];
  
  // Sanitize the record
  const sanitizedRecord: CsvRecord = {
    first_name: record.first_name?.toString().trim() || '',
    last_name: record.last_name?.toString().trim() || '',
    email: record.email?.toString().toLowerCase().trim() || '',
    role: record.role?.toString().trim() || '',
    organization: record.organization?.toString().trim() || ''
  };
  
  // Required fields validation
  if (!sanitizedRecord.first_name) {
    errors.push('First name is required');
  } else if (sanitizedRecord.first_name.length > 50) {
    errors.push('First name must be 50 characters or less');
  }
  
  if (!sanitizedRecord.last_name) {
    errors.push('Last name is required');
  } else if (sanitizedRecord.last_name.length > 50) {
    errors.push('Last name must be 50 characters or less');
  }
  
  if (!sanitizedRecord.email) {
    errors.push('Email is required');
  } else {
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedRecord.email)) {
      errors.push('Invalid email format');
    }
  }
  
  if (!sanitizedRecord.organization) {
    errors.push('Organization is required');
  }
  
  // Role validation
  if (sanitizedRecord.role && !['coach', 'parent', 'player'].includes(sanitizedRecord.role)) {
    errors.push('Invalid role. Must be one of: coach, parent, player');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitizedRecord: errors.length === 0 ? sanitizedRecord : undefined
  };
};

export const validateForDuplicates = async (records: Partial<CsvRecord>[]): Promise<{
  duplicateEmails: string[];
  internalDuplicates: string[];
}> => {
  const duplicateEmails: string[] = [];
  const internalDuplicates: string[] = [];
  const emailCounts = new Map<string, number>();
  
  // Check for internal duplicates within the CSV
  records.forEach(record => {
    if (record.email) {
      const email = record.email.toLowerCase().trim();
      const count = emailCounts.get(email) || 0;
      emailCounts.set(email, count + 1);
      
      if (count > 0 && !internalDuplicates.includes(email)) {
        internalDuplicates.push(email);
      }
    }
  });
  
  return {
    duplicateEmails,
    internalDuplicates
  };
};
